/**
 * chat.js
 * Chat management functions (create, load, delete, filter)
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { renderEmptyState, renderChatList, showToast } from './ui.js?v=302';
import { renderMessage } from './messages.js?v=302';
import { generateResponse } from './api2.js?v=302';
import { saveChats, clearDraft } from './storage.js?v=302';
import { autoResizeInput, updateSendButton } from './utils.js?v=302';

/**
 * Create a new chat conversation
 */
export function createNewChat() {
  // Cancel any ongoing generation first
  if (state.isGenerating && state.controller) {
    state.controller.abort();
    state.isGenerating = false;
    state.controller = null;
  }

  state.currentChatId = null;
  localStorage.removeItem("chatbot_current_chat");

  renderEmptyState();
  renderChatList();
  elements.input.value = "";

  // Update attachments - dynamically import to avoid circular deps
  import('./files/files.js?v=302').then(({ updateAttachments }) => {
    updateAttachments([]);
  }).catch(() => {
    // Fallback if files module not loaded yet
    state.attachedFiles = [];
  });
}

/**
 * Load and display a specific chat
 * @param {string} chatId - ID of the chat to load
 */
export function loadChat(chatId) {
  const chat = state.chats.find((c) => c.id === chatId);
  if (!chat) return;

  state.currentChatId = chatId;
  localStorage.setItem("chatbot_current_chat", chatId);

  // Clear messages and render chat
  elements.messagesContainer.innerHTML = "";
  chat.messages.forEach((msg, index) => {
    const prevMsg = index > 0 ? chat.messages[index - 1] : null;
    renderMessage(msg, prevMsg);
  });
  renderChatList();
}

/**
 * Delete a chat conversation
 * @param {string} chatId - ID of the chat to delete
 */
export function deleteChat(chatId) {
  state.chats = state.chats.filter((c) => c.id !== chatId);
  saveChats();

  if (state.currentChatId === chatId) {
    state.currentChatId = null;
    localStorage.removeItem("chatbot_current_chat");
    renderEmptyState();
  }

  renderChatList();
  showToast("Conversation deleted");
}

/**
 * Filter chat list based on search query
 */
export function filterChatList() {
  const query = elements.searchChats.value.toLowerCase();
  const items = elements.chatList.querySelectorAll(".chat-item");

  items.forEach((item) => {
    const text = item.textContent.toLowerCase();
    item.style.display = text.includes(query) ? "flex" : "none";
  });
}

/**
 * Send a message in the current or new chat
 */
export async function sendMessage() {
  const content = elements.input.value.trim();
  if (!content && state.attachedFiles.length === 0) {
    return;
  }
  if (state.isGenerating) {
    return;
  }

  const messageContent = content;
  elements.input.value = "";
  clearDraft();
  autoResizeInput();

  // Remove empty state and render message
  const emptyState = elements.messagesContainer.querySelector(".empty-state");
  if (emptyState) {
    emptyState.remove();
  }

  // Create or get current chat
  let currentChat;
  if (state.currentChatId) {
    currentChat = state.chats.find((c) => c.id === state.currentChatId);
  }

  if (!currentChat) {
    // Generate smart title from message content
    let titleText = messageContent;
    
    // Strip common prefixes
    const prefixes = ['Can you', 'Please', 'I need', 'Could you', 'Help me', 'Can I', 'Would you'];
    for (const prefix of prefixes) {
      if (titleText.toLowerCase().startsWith(prefix.toLowerCase())) {
        titleText = titleText.substring(prefix.length).trim();
        break;
      }
    }
    
    // Trim at word boundary (max 40 chars)
    let title = titleText;
    if (titleText.length > 40) {
      title = titleText.substring(0, 40);
      const lastSpace = title.lastIndexOf(' ');
      if (lastSpace > 20) { // Only trim at space if we're not cutting too much
        title = title.substring(0, lastSpace);
      }
      title += '...';
    }
    
    currentChat = {
      id: Date.now().toString(),
      title: title,
      messages: [],
      createdAt: new Date().toISOString(),
    };
    state.chats.unshift(currentChat);
    state.currentChatId = currentChat.id;
  }

  // Add user message
  const userMessage = {
    id: Date.now().toString(),
    role: "user",
    content: messageContent,
    files: state.attachedFiles,
    timestamp: new Date().toISOString(),
  };
  currentChat.messages.push(userMessage);

  const prevMsg = currentChat.messages.length > 1 ? currentChat.messages[currentChat.messages.length - 2] : null;
  renderMessage(userMessage, prevMsg);
  renderChatList();
  saveChats();

  // Update attachments - dynamically import to avoid circular deps
  import('./files/files.js?v=302').then(({ updateAttachments }) => {
    updateAttachments([]);
  }).catch(() => {
    // Fallback if files module not loaded yet
    state.attachedFiles = [];
  });

  // Generate response
  await generateResponse(currentChat);
}
